import TabPanel from '@components/TabPanel'
import { Principal } from '@dfinity/principal'
import { Alert, Stack, Tab, Tabs, Typography } from '@mui/material'
import { base64ToArrayBuffer } from '@plug-core/utils/arrayBuffer'
import { decodeCallArgs } from '@utils/decodeArgs/decodeCallArgs'
import { getCandid } from '@utils/decodeArgs/downloadCandid'
import { fetchCanisterConsentMessage } from '@utils/fetchCanisterConsentMessage'
import React, { FC, useEffect, useState } from 'react'
import { CodeBlock, dracula } from 'react-code-blocks'
import Markdown from 'react-markdown'
import classes from './CanisterCall.module.scss'
import { logger } from '@plug-core/logger'

interface CanisterCallProps {
  canisterId: string
  method: string
  arg: string
}

const CanisterCall: FC<CanisterCallProps> = ({ canisterId, method, arg }) => {
  const [decodedArguments, setDecodedArguments] = useState<string>()
  const [currentTab, setCurrentTab] = useState(0)
  const [noConsent, setNoConsent] = useState(false)
  const [consentMessage, setConsentMessage] = useState<string>()

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue)
  }

  useEffect(() => {
    getCandid(Principal.from(canisterId))
      .then((candid) => {
        return decodeCallArgs(method, arg, candid)
      })
      .then(setDecodedArguments)
      .catch((e) => {
        logger.error('Failed to decode arguments', { originalError: e })
      })
  }, [canisterId])

  useEffect(() => {
    fetchCanisterConsentMessage(canisterId, method, base64ToArrayBuffer(arg))
      .then(setConsentMessage)
      .catch(() => {
        setNoConsent(true)
      })
  }, [canisterId, method, arg])

  const renderPropertyRow = (title: string, value: string) => (
    <Stack spacing={1}>
      <Typography variant="body2">{title}</Typography>
      <Stack
        bgcolor={(theme) => theme.palette.background.paper}
        sx={{ borderRadius: 1 }}
        px={2}
        py={1}
      >
        <Typography>{value}</Typography>
      </Stack>
    </Stack>
  )

  return (
    <Stack m={2}>
      <Tabs value={currentTab} onChange={handleChange}>
        <Tab sx={{ flex: 1 }} label="Info" />
        <Tab sx={{ flex: 1 }} label="Parameters" />
      </Tabs>
      <TabPanel value={currentTab} index={0}>
        <Stack spacing={1}>
          {noConsent && (
            <Alert severity="warning" sx={{ alignItems: 'center' }}>
              Canister did not provide consent message - use at own risk
            </Alert>
          )}
          {consentMessage && (
            <Stack
              bgcolor={(theme) => theme.palette.background.paper}
              p={2}
              borderRadius={2}
              className={classes.consentHolder}
            >
              <Markdown>{consentMessage}</Markdown>
            </Stack>
          )}
          {renderPropertyRow('Canister ID', canisterId)}
          {renderPropertyRow('Method', method)}
        </Stack>
      </TabPanel>
      <TabPanel value={currentTab} index={1}>
        <Stack>
          <Typography variant="body2" mb={1}>
            Parameters
          </Typography>
          <CodeBlock
            text={decodedArguments}
            language="perl"
            theme={dracula}
            showLineNumbers={false}
            codeContainerStyle={{ height: 150 }}
          />
        </Stack>
      </TabPanel>
    </Stack>
  )
}

export default CanisterCall
